import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { getApiBaseUrl } from '@/lib/api-base-url';
import { authFetch } from '@/lib/auth-fetch';

type DecimalString = string | null;

export interface HedgeBookExecution {
  id: string;
  executionType: string;
  direction: string;
  quantityMt: string;
  // This request's allocated share of the fill (null/absent when the execution
  // has no allocation for the request). For multi-trade fills quantityMt is the
  // whole position; this is the slice booked to this request. Prefer it when
  // showing a per-request quantity, falling back to quantityMt.
  allocatedQuantityMt?: DecimalString;
  executedPrice: string;
  executedPriceCurrency: string;
  fixingPrice: DecimalString;
  promptDate: string | null;
  executionDate: string;
  instrument: string;
  exchange: string | null;
  brokerName: string | null;
  contractReference: string | null;
  notes: string | null;
  createdAt: string;
}

export interface HedgeBookEntry {
  id: string;
  status: string;
  source: string;
  reason: string | null;
  direction: string;
  quantityMt: string;
  hedgeMetal: string | null;
  instrumentType: string | null;
  currencyPair: string | null;
  scopeLevel: string;
  orderId: string | null;
  blOrderId: number | null;
  ticketId: number | null;
  estimatedQpMonth: string | null;
  targetPromptDate: string | null;
  reference: string;
  targetPrice: DecimalString;
  targetPriceCurrency: string | null;
  brokerPreference: string | null;
  notes: string | null;
  rollFromExecutionId: string | null;
  createdAt: string;
  updatedAt: string;
  qpStartAnchor: string | null;
  qpStartOffsetDays: number | null;
  qpEndAnchor: string | null;
  qpEndOffsetDays: number | null;
  netExecutedMt: DecimalString;
  openQuantityMt: DecimalString;
  executionCount: number;
  executions: HedgeBookExecution[];
}

export interface HedgeBookPage {
  entries: HedgeBookEntry[];
  total: number;
  page: number;
  pageSize: number;
}

export interface HedgeBookQuery {
  status?: string | undefined;
  metal?: string | undefined;
  scope?: string | undefined;
  instrument?: 'METAL' | 'FX' | undefined;
  search?: string | undefined;
  page?: number | undefined;
  pageSize?: number | undefined;
}

export class HedgingHedgeBookApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
  ) {
    super(message);
    this.name = 'HedgingHedgeBookApiError';
  }
}

function buildQueryString(query: HedgeBookQuery): string {
  const params = new URLSearchParams();
  if (query.status) params.set('status', query.status);
  if (query.metal) params.set('metal', query.metal);
  if (query.scope) params.set('scope', query.scope);
  if (query.instrument) params.set('instrument', query.instrument);
  if (query.search) params.set('search', query.search);
  if (query.page !== undefined) params.set('page', String(query.page));
  if (query.pageSize !== undefined) params.set('pageSize', String(query.pageSize));
  const s = params.toString();
  return s.length === 0 ? '' : `?${s}`;
}

async function fetchHedgeBook(query: HedgeBookQuery): Promise<HedgeBookPage> {
  const response = await authFetch(
    `${getApiBaseUrl()}/hedging/hedge-book${buildQueryString(query)}`,
  );
  if (!response.ok) {
    throw new HedgingHedgeBookApiError(
      `Request failed (${String(response.status)})`,
      response.status,
    );
  }
  return (await response.json()) as HedgeBookPage;
}

export const HEDGING_HEDGE_BOOK_QUERY_KEY = 'hedging:hedge-book';

export function useHedgeBook(query: HedgeBookQuery) {
  return useQuery({
    queryKey: [HEDGING_HEDGE_BOOK_QUERY_KEY, query],
    queryFn: () => fetchHedgeBook(query),
    placeholderData: keepPreviousData,
  });
}
